import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { Predictions } from "src/shared/lib/amplify";
import { TranslateTextCommand } from "@aws-sdk/client-translate";
import { getTranslateClient, executeWithRetry } from "src/utils/translationHelper";

const languageFontMap = new Map([
  ["ko", ["cc_custom_terminology"]]
]);

interface TranslationRequestPayload {
  text: string;
  targetLanguage?: string;
}

export const getTranslation = createAsyncThunk<any, TranslationRequestPayload>(
  "translations/getTranslation",
  async (data, thunkApi) => {
    const { translationSlice: { data: translationStateData }, userData } = thunkApi.getState() as any;
    const targetLanguage =
      data?.targetLanguage || userData.selectedLanguage || "en";
    const cacheKey = `${targetLanguage}::${data.text}`;

    if (translationStateData[cacheKey]) {
      return translationStateData[cacheKey];
    }

    try {
      if (languageFontMap.has(targetLanguage)) {
        const client = await getTranslateClient();
        if (!client) {
          return data.text;
        }
        const command = new TranslateTextCommand({
          Text: data.text,
          SourceLanguageCode: "en",
          TargetLanguageCode: targetLanguage,
          TerminologyNames: languageFontMap.get(targetLanguage) || []
        });

        const response = await executeWithRetry(() => client.send(command));

        if (!response || !response.TranslatedText) {
          throw new Error("Invalid AWS translation response");
        }
        return response.TranslatedText;
      } else {
        const response = await Predictions.convert({
          translateText: {
            source: {
              text: data.text
            },
            targetLanguage
          }
        })
        return response.text;
      }
    } catch (error: any) {
      console.log(error)
      const message = error.message;
      return thunkApi.rejectWithValue(message);
    }
  }
);

export interface TranslationState {
  loading: boolean;
  error: string | null;
  data: Record<string, string>;
}

const initialState = {
  loading: false,
  error: null,
  data: {},
} as TranslationState;

export const translationSlice = createSlice({
  name: "post",
  initialState,
  reducers: {
    clearTranslations: (state) => {
      state.data = {}
    },
  },

  extraReducers(builder) {
    builder
      .addCase(getTranslation.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTranslation.fulfilled, (state, action: any) => {
        state.loading = false;
        const targetLanguage = action?.meta?.arg?.targetLanguage || "en";
        const text = action?.meta?.arg?.text || "";
        const cacheKey = `${targetLanguage}::${text}`;
        state.data[cacheKey] = action.payload;
      })
      .addCase(getTranslation.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload;
      });
  }
});

export const { clearTranslations } = translationSlice.actions;
export default translationSlice.reducer;
